/**
 * Trend Chart Data Hook
 * 
 * Custom hook for managing trend chart data including loading,
 * error handling, and date range filtering.
 */

import { useState, useEffect, useCallback } from 'react';
import { TechnologyType, TrendChartData, DateRange } from '../services/equipment/types';
import { measurementsService } from '../services/equipment/measurements-service';
import { useDateRange } from './use-date-range';

interface UseTrendChartDataOptions {
  site?: string;
  /** Equipment ID to fetch data for */
  equipmentId?: string;
  position?: string;
  /** Technology type to fetch data for */
  technology: TechnologyType;
  /** Auto-fetch on mount */
  autoFetch?: boolean;
  /** Whether to use date range filtering from context */
  useDateRangeFiltering?: boolean;
}

interface UseTrendChartDataReturn {
  /** Chart data */
  data: TrendChartData | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Current date range being used for filtering */
  dateRange: DateRange | null;
  /** Fetch data function */
  fetchData: (site: string, equipmentId: string, position: string, dateRange?: DateRange) => Promise<void>;
  /** Refresh data function */
  refresh: () => Promise<void>;
  /** Clear data and reset state */
  clear: () => void;
}

/**
 * Hook for managing trend chart data
 */
export const useTrendChartData = (options: UseTrendChartDataOptions): UseTrendChartDataReturn => {
  const {
    site,
    equipmentId,
    position,
    technology,
    autoFetch = true,
    useDateRangeFiltering = true
  } = options;

  // Get date range from context if enabled
  const { dateRange: contextDateRange } = useDateRangeFiltering ? useDateRange() : { dateRange: null };

  const [data, setData] = useState<TrendChartData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSite, setCurrentSite] = useState<string | undefined>(site);
  const [currentEquipmentId, setCurrentEquipmentId] = useState<string | undefined>(equipmentId);
  const [currentPosition, setCurrentPosition] = useState<string | undefined>(position);
  const [currentDateRange, setCurrentDateRange] = useState<DateRange | null>(contextDateRange);

  /**
   * Fetch trend chart data for a specific equipment ID
   */
  const fetchData = useCallback(async (
    targetSite: string,
    targetEquipmentId: string,
    targetPosition: string,
    targetDateRange?: DateRange
  ) => {
    if (!targetSite || !targetEquipmentId || !targetPosition) return;

    setIsLoading(true);
    setError(null);

    try {
      const dateRangeToUse = targetDateRange || (useDateRangeFiltering ? contextDateRange : null);
      
      const chartData = await measurementsService.getTrendChartData(
        targetSite,
        targetEquipmentId,
        targetPosition,
        technology,
        dateRangeToUse || undefined
      );
      
      setData(chartData);
      setCurrentSite(targetSite);
      setCurrentEquipmentId(targetEquipmentId);
      setCurrentPosition(targetPosition);
      setCurrentDateRange(dateRangeToUse);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to fetch ${technology} data`;
      setError(errorMessage);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [technology, contextDateRange, useDateRangeFiltering]);

  /**
   * Refresh current data
   */
  const refresh = useCallback(async () => {
    if (!currentSite || !currentEquipmentId || !currentPosition) return;
    await fetchData(currentSite, currentEquipmentId, currentPosition, currentDateRange || undefined);
  }, [currentSite, currentEquipmentId, currentPosition, currentDateRange, fetchData]);

  /**
   * Clear data and reset state
   */
  const clear = useCallback(() => {
    setData(null);
    setError(null);
    setCurrentSite(undefined);
    setCurrentEquipmentId(undefined);
    setCurrentPosition(undefined);
    setCurrentDateRange(null);
  }, []);

  // Auto-fetch when equipmentId changes
  useEffect(() => {
    if (
        autoFetch && site && equipmentId && position &&
        (site !== currentSite || equipmentId !== currentEquipmentId || position !== currentPosition)
    ) {
      fetchData(site, equipmentId, position);
    }
  }, [site, equipmentId, currentEquipmentId, position, autoFetch, fetchData]);

  // Clear data when equipmentId is removed
  useEffect(() => {
    if ((!site && currentSite) || (!equipmentId && currentEquipmentId) || (!position && currentPosition)) {
      clear();
    }
  }, [site, currentSite, equipmentId, currentEquipmentId, position, currentPosition, clear]);

  // Refetch data when date range changes (if using date range filtering)
  useEffect(() => {
    if (
      useDateRangeFiltering && 
      contextDateRange &&
      currentSite &&
      currentEquipmentId &&
      currentPosition &&
      contextDateRange !== currentDateRange
    ) {
      fetchData(currentSite, currentEquipmentId, currentPosition, contextDateRange);
    }
  }, [contextDateRange, currentDateRange, currentSite, currentEquipmentId, currentPosition, useDateRangeFiltering, fetchData]);

  return {
    data,
    isLoading,
    error,
    dateRange: currentDateRange,
    fetchData,
    refresh,
    clear
  };
};

/**
 * Hook for managing all technology trend chart data
 */
export const useAllTrendChartData = (options: Omit<UseTrendChartDataOptions, 'technology'>) => {  
  // Use individual hooks for each technology
  const monitronData = useTrendChartData({ ...options, technology: 'monitron' });
  const pulseData = useTrendChartData({ ...options, technology: 'pulse' });
  const cadetData = useTrendChartData({ ...options, technology: 'cadet' });
  const thermalData = useTrendChartData({ ...options, technology: 'thermal' });

  const allData = {
    monitron: monitronData,
    pulse: pulseData,
    cadet: cadetData,
    thermal: thermalData,
  };

  // Aggregate loading and error states
  const isLoading = Object.values(allData).some(data => data.isLoading);
  const hasError = Object.values(allData).some(data => data.error !== null);
  const errors = Object.entries(allData)
    .filter(([, data]) => data.error)
    .map(([tech, data]) => `${tech}: ${data.error}`)
    .join(', ');

  // Refresh all data
  const refreshAll = useCallback(async () => {
    await Promise.all(Object.values(allData).map(data => data.refresh()));
  }, [allData]);

  // Clear all data
  const clearAll = useCallback(() => {
    Object.values(allData).forEach(data => data.clear());
  }, [allData]);

  return {
    data: allData,
    isLoading,
    hasError,
    errors: hasError ? errors : null,
    refreshAll,
    clearAll,
  };
};